import { memo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import "./HomeLayout.scss";
import { DynamicBlocksRendererContainer } from "app/pages/.shared/DynamicBlocks";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import HeaderContainer from "app/pages/Header/HeaderContainer";
import classNames from "classnames";
import SmartDPSearchHeadMetadata from "app/pages/SmartDP/Search/SmartDPSearchHeadMetadata";
import WebsiteTunnelSwitcherContainer from "app/pages/.shared/WebsiteTunnelSwitcher/WebsiteTunnelSwitcherContainer";
import { isEmpty } from "lodash";
import SmartDPHeroCommercial from "app/pages/SmartDP/SmartDPHeroCommercial/SmartDPHeroCommercial";
import SmartDPHeroCommercialCarousel from "app/pages/SmartDP/SmartDPHeroCommercialCarousel/SmartDPHeroCommercialCarousel";
import Loader from "app/pages/.shared/Loader";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import SmartDPMarketingHeroImageContainer from "app/pages/SmartDP/SmartDPMarketingHeroImage/SmartDPMarketingHeroImageContainer";
import SmartDPMarketingHeadlineContainer from "app/pages/SmartDP/SmartDPMarketingHeadline/SmartDPMarketingHeadlineContainer";

const HomeLayout = ({
	websiteTunnels = [],
	homeBlocks = [],
	isBrandWithCustomHeader,
	heroCommercialMerch = {},
	isTransparentHeader,
	brandCode,
	heroCommercialCode,
	carouselMerchandisings = [],
}) => {
	const smartDPSearchRef = useRef(null);
	const { track } = useAnalytics();
	const heroCommercialCarouselAvailable = carouselMerchandisings?.length > 1;
	const isHeroCommercialAvailable =
		!isEmpty(heroCommercialMerch) || heroCommercialCarouselAvailable;

	useEffect(() => {
		track("sdp_search_page", {
			heroCommercialCount: carouselMerchandisings?.length,
		});
	}, [carouselMerchandisings?.length]);

	const containerClass = classNames({
		"home-layout__container": true,
		"home-layout__container--custom-header": isBrandWithCustomHeader || !isTransparentHeader,
	});

	const homeLayoutHeroCommercialClass = classNames("home-layout__hero-commercial", {
		"home-layout__hero-commercial--image":
			!isEmpty(heroCommercialMerch) && !heroCommercialCarouselAvailable,
		"home-layout__hero-commercial--carousel": heroCommercialCarouselAvailable,
	});

	const displayLoading = heroCommercialCode && isEmpty(heroCommercialMerch);

	return (
		<div ref={smartDPSearchRef} className="home-layout">
			<SmartDPSearchHeadMetadata />
			<HeaderContainer brandCode={brandCode} />
			<div className={containerClass}>
				{displayLoading ? (
					<div className="home-layout__loader">
						<Loader />
					</div>
				) : (
					<div className={homeLayoutHeroCommercialClass}>
						{isHeroCommercialAvailable ? (
							<>
								{heroCommercialCarouselAvailable ? (
									<SmartDPHeroCommercialCarousel
										merchandisings={carouselMerchandisings}
									/>
								) : (
									<div className="home-layout__hero-commercial-image">
										<SmartDPHeroCommercial
											heroCommercialMerch={heroCommercialMerch}
										/>
									</div>
								)}
							</>
						) : (
							<>
								<div className="home-layout__hero-commercial-image home-layout__hero-commercial-image--generic">
									<SmartDPMarketingHeroImageContainer
										isBackgroundProgressiveImg={true}
									/>
								</div>
								<div className="home-layout__hero-commercial-headline-generic">
									<SmartDPMarketingHeadlineContainer />
								</div>
							</>
						)}
						<div className="home-layout__hero-commercial-form">
							<WebsiteTunnelSwitcherContainer
								websiteTunnels={websiteTunnels}
								disableFormPersistance={false}
							/>
						</div>
					</div>
				)}
				<div className="home-layout__content">
					<div className="home-layout__dynamic-blocks">
						<DynamicBlocksRendererContainer blocks={homeBlocks} />
					</div>
				</div>
			</div>
			<PushNotificationContainer />
		</div>
	);
};

HomeLayout.propTypes = {
	websiteTunnels: PropTypes.array,
	homeBlocks: PropTypes.array,
	heroCommercialCode: PropTypes.string,
	isBrandWithCustomHeader: PropTypes.bool,
	heroCommercialMerch: PropTypes.object,
	brandCode: PropTypes.string,
	isTransparentHeader: PropTypes.bool,
	carouselMerchandisings: PropTypes.array,
};

export default memo(HomeLayout);
